import FranchiseeForm from "components/Common/NewFranchiseForm";
import { useAlert } from "context/alert";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";
import AuthorizationCertificate from "components/Common/FranchiseFormat";
import {
  FaCertificate,
  FaFileDownload,
  FaFileSignature,
  FaIdCard,
} from "react-icons/fa";
import FranchiseAgreement from "components/Common/Agreement/Agreement";
import html2pdf from "html2pdf.js";
import DocumentAuthorizationCertificate from "components/Common/FranchiseFormat/DocumentsCertificateFormat";

const DocumentsDownload = ({ onClose, onKycVerification }) => {
  const { showAlert } = useAlert();
  const formRef = useRef(null);
  const certificateRef = useRef(null);
  const agreementRef = useRef(null);

  const handleGenerateAuthorizationCertificate = async () => {
    if (certificateRef.current) {
      try {
        const canvas = await html2canvas(certificateRef.current, {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: 0,
          width: certificateRef.current.scrollWidth,
          height: certificateRef.current.scrollHeight,
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: "a4",
        });

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const margin = 1.76;

        const imgWidth = pdfWidth - margin * 2;
        const imgHeight = pdfHeight - margin * 2;

        pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight);

        pdf.save("AuthorizationCertificate.pdf");

        showAlert("Authorization Certificate downloaded as high-quality PDF.");
      } catch (error) {
        console.error("Error generating PDF:", error);
        showAlert("Error generating PDF. Please try again.");
      }
    }
  };

  const handleDownloadFranchiseeForm = async () => {
    if (formRef.current) {
      const canvas = await html2canvas(formRef.current, {
        scale: 3,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("FranchiseeForm.pdf");

      showAlert("Franchisee Form downloaded as PDF.");
    }
  };

  const handleDownloadAgreementCopy = () => {
    if (agreementRef.current) {
      const element = agreementRef.current;

      const opt = {
        margin: [10, 15, 10, 15],
        filename: "Franchise_Agreement.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      };

      html2pdf().set(opt).from(element).save();
      showAlert("Franchise Agreement downloaded as multi-page PDF.");
    }
  };

  const handleKycVerification = () => {
    onKycVerification();
  };

  const cardData = [
    {
      title: "Generate Authorization Certificate",
      icon: <FaCertificate size={32} />,
      onClick: handleGenerateAuthorizationCertificate,
      color: "rgb(13 110 253 / 35%)",
    },
    {
      title: "Download New Franchisee Form",
      icon: <FaFileDownload size={32} />,
      onClick: handleDownloadFranchiseeForm,
      color: "rgb(25 135 84 / 43%)",
    },
    {
      title: "Download Agreement Copy",
      icon: <FaFileSignature size={32} />,
      onClick: handleDownloadAgreementCopy,
      color: "rgb(255 193 7 / 26%)",
    },
    {
      title: "KYC Verification",
      icon: <FaIdCard size={32} />,
      onClick: handleKycVerification,
      color: "rgb(220 53 69 / 38%)",
    },
  ];

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        {cardData.map((card, index) => (
          <div key={index} className="col-12 col-md-6 mb-4">
            <div
              className={`card border-0 shadow-sm`}
              role="button"
              onClick={card.onClick}
              style={{
                height: "150px",
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `${card.color}`,
                color: "black",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.03)";
                e.currentTarget.style.boxShadow =
                  "0 12px 20px rgba(0, 0, 0, 0.25), 0 6px 6px rgba(0, 0, 0, 0.22)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow =
                  "0 1px 4px rgba(0, 0, 0, 0.1)";
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "scale(0.98)";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "scale(1.03)";
              }}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center">
                <div className="mb-2">{card.icon}</div>
                <h5 className="card-title mb-0" style={{ fontSize: "1.25rem" }}>
                  {card.title}
                </h5>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: "794px",
          padding: "20px",
          background: "white",
        }}
      >
        <div ref={formRef}>
          <FranchiseeForm />
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: "1123px",
          padding: "20px",
          background: "white",
        }}
      >
        <div ref={certificateRef}>
          <DocumentAuthorizationCertificate />
        </div>
      </div>
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: "794px",
          background: "white",
        }}
      >
        <div ref={agreementRef}>
          <FranchiseAgreement />
        </div>
      </div>
    </div>
  );
};

export default DocumentsDownload;
